import type { RefObject } from "react";

import type { Dayjs } from "@eisox/dayjs";
import { createContext } from "@radix-ui/react-context";

import type { DateRange } from "../Calendar";
import type { DateFieldFormat, DateFieldSharedProps, DateFieldUnit, RangePart } from "./date-field.type";
import { toPeriodPart } from "./helpers/date-range.helper";

const DATE_FIELD_NAME = "DateField";

interface SharedContext extends Pick<
  DateFieldSharedProps,
  "min" | "max" | "disabled" | "readOnly" | "error" | "rounded"
> {
  unit: DateFieldUnit;
  format: DateFieldFormat;
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorRef: RefObject<HTMLDivElement | null>;
}

type DateFieldContextValue = SharedContext &
  (
    | { mode: "single"; value: Dayjs | null; onValueChange: (value: Dayjs | null) => void }
    | {
        mode: "range";
        value: DateRange;
        onValueChange: (value: DateRange) => void;
        activePart: RangePart;
        setActivePart: (part: RangePart) => void;
      }
  );

const [DateFieldProvider, useDateFieldContext] = createContext<DateFieldContextValue>(DATE_FIELD_NAME);

const [InsideInputProvider, useInsideInputContext] = createContext<{
  inside: boolean;
  part?: RangePart;
  labelledBy?: string;
}>(DATE_FIELD_NAME, {
  inside: false,
});

interface FieldValue {
  value: Dayjs | null;
  onValueChange: (value: Dayjs | null) => void;
  active: boolean;
}

const resolveFieldValue = (context: DateFieldContextValue, part: RangePart): FieldValue => {
  const toPeriod = (next: Dayjs | null) => next && toPeriodPart(next, context.unit, part, context);

  if (context.mode === "single") {
    return { value: context.value, onValueChange: next => context.onValueChange(toPeriod(next)), active: false };
  }

  return {
    value: context.value[part],
    onValueChange: next => context.onValueChange({ ...context.value, [part]: toPeriod(next) }),
    active: context.activePart === part,
  };
};

export {
  DATE_FIELD_NAME,
  DateFieldProvider,
  InsideInputProvider,
  resolveFieldValue,
  useDateFieldContext,
  useInsideInputContext,
};
export type { DateFieldContextValue, SharedContext };
